import { useMemo } from "react";
import { useTimelineCtx } from "../ctx/timeline-ctx";
import {
  TimelineMarkerEnum,
  TimelinePositionEnum,
  TimelineStyleEnum,
} from "../types";
import { useTimelineIsItemOutside } from "./use-timeline-is-item-outside";
import { useTimelineIsLast } from "./use-timeline-is-last";

interface IProps {
  itemIdx: number;
}

export type ITimelineItemDetails = ReturnType<typeof useTimelineItemDetails>;

export const useTimelineItemDetails = ({ itemIdx }: IProps) => {
  const {
    isVertical,
    itemsMovedBy,
    itemsInView,
    totalAmtOfItems,
    isMobile,
    comboSettings: {
      timelinePosition,
      timelineStyle,
      timelineMarker,
      timelineContentPlacement,
    },
  } = useTimelineCtx();
  const isOutside = useTimelineIsItemOutside(itemIdx);
  const isLast = useTimelineIsLast(itemIdx);

  return useMemo(() => {
    const idxInView = (itemIdx - itemsMovedBy) % itemsInView;

    let trackPr = 16;
    let trackPl = 16;
    const trackPx = trackPl + trackPr;
    let trackWidth = 20;

    let isContentOnLeft = false;
    let isTrackInCenter = false;

    let contentWidth = "100%";

    if (isVertical) {
      if (timelinePosition === TimelinePositionEnum.Right) {
        isContentOnLeft = true;
      } else if (timelinePosition === TimelinePositionEnum.Center) {
        // Center alternates by default; presets opt out via
        // `timelineContentPlacement: "default"`. Legacy slides have it
        // undefined — keep the alternating fallback for back-compat.
        isContentOnLeft =
          timelineContentPlacement === "default" ? false : itemIdx % 2 === 0;
        isTrackInCenter = true;
      }

      if (isMobile) {
        if (isTrackInCenter || !isContentOnLeft) {
          trackPr = 0;
        } else {
          trackPl = 0;
        }

        isTrackInCenter = false;
      }

      if (timelineStyle === TimelineStyleEnum.Path) {
        trackWidth = 140;
      } else if (timelineMarker === TimelineMarkerEnum.CustomText) {
        trackWidth = 128;
      }
    }

    trackWidth += trackPx;

    if (!isMobile && isTrackInCenter) {
      contentWidth = `calc(50% - ${trackWidth}px / 2)`;
    }

    return {
      isOutside,
      idxInView,
      isLast,
      isLastInView: idxInView === itemsInView - 1 || isLast,

      trackWidth,
      trackPl,
      trackPr,
      trackPx,

      isContentOnLeft,
      isTrackInCenter,

      contentWidth,
    };
  }, [
    itemIdx,
    isOutside,
    isLast,
    isVertical,
    itemsMovedBy,
    itemsInView,
    totalAmtOfItems,
    isMobile,
    timelinePosition,
    timelineStyle,
    timelineMarker,
    timelineContentPlacement,
  ]);
};
